import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, Phone, MapPin, Send, Facebook, Twitter, Instagram, Linkedin, Youtube } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useSiteData } from '../contexts/SiteDataContext';
import { submitContact } from '../utils/api';
import { useToast } from '../hooks/use-toast';

const ContactSection = () => {
  const { author } = useSiteData();
  const authorData = author || {};
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact(formData);
      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const socialLinks = [
    { icon: Facebook, href: authorData.facebook, label: 'Facebook', color: 'hover:text-blue-600' },
    { icon: Twitter, href: authorData.twitter, label: 'Twitter', color: 'hover:text-sky-500' },
    { icon: Instagram, href: authorData.instagram, label: 'Instagram', color: 'hover:text-pink-600' },
    { icon: Linkedin, href: authorData.linkedin, label: 'LinkedIn', color: 'hover:text-blue-700' },
    { icon: Youtube, href: authorData.youtube, label: 'YouTube', color: 'hover:text-red-600' },
  ];

  return (
    <section id="contact" ref={ref} className="py-24 bg-gradient-to-br from-blue-50 via-white to-orange-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
            संपर्क करें
          </h2>
          <p 
            className="text-xl text-orange-600 font-semibold"
            style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
          >
            आपके संदेश का स्वागत है
          </p>
          <p className="text-gray-600 mt-4" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>कोई प्रश्न है? हमें आपसे सुनकर प्रसन्नता होगी!</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-orange-100">
              <h3 className="text-2xl font-bold text-blue-900 mb-6" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>संदेश भेजें</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>नाम *</label>
                  <Input 
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name-input"
                    className="w-full"
                    placeholder="आपका पूरा नाम"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>ईमेल *</label>
                  <Input 
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email-input"
                    className="w-full"
                    placeholder="aapka.email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>फोन नंबर</label>
                  <Input 
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full"
                    placeholder="+91 89834 17064"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>संदेश *</label>
                  <Textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    data-testid="contact-message-input"
                    className="w-full"
                    placeholder="अपना संदेश यहाँ लिखें..."
                  />
                </div>
                <Button 
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <Send className="mr-2 h-5 w-5" />
                  {submitting ? 'भेजा जा रहा है...' : 'संदेश भेजें'}
                </Button>
              </form>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Contact Details */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-orange-100">
              <h3 className="text-2xl font-bold text-blue-900 mb-6" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>संपर्क जानकारी</h3>
              <div className="space-y-6">
                <motion.div 
                  className="flex items-start gap-4 group"
                  whileHover={{ x: 10, transition: { duration: 0.3 } }}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>ईमेल</div>
                    <a href={`mailto:${authorData.email}`} className="text-blue-900 font-semibold hover:text-orange-600 transition-colors">
                      {authorData.email}
                    </a>
                  </div>
                </motion.div>

                <motion.div 
                  className="flex items-start gap-4 group"
                  whileHover={{ x: 10, transition: { duration: 0.3 } }}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>मोबाइल</div>
                    <a href={`tel:${authorData.phone}`} className="text-blue-900 font-semibold hover:text-orange-600 transition-colors">
                      {authorData.phone}
                    </a>
                  </div>
                </motion.div>

                <motion.div 
                  className="flex items-start gap-4 group"
                  whileHover={{ x: 10, transition: { duration: 0.3 } }}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>स्थान</div>
                    <div className="text-blue-900 font-semibold" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>भारत</div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>सोशल मीडिया पर जुड़ें</h3>
              <div className="flex flex-wrap gap-4">
                {socialLinks.filter((s) => s.href).map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className={`w-14 h-14 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center text-white ${social.color} transition-all duration-300`}
                  >
                    <social.icon className="h-6 w-6" />
                  </motion.a>
                ))}
              </div>
            </div>

            {/* WhatsApp Button */}
            <motion.a
              href={`https://wa.me/${authorData.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              className="block"
            >
              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 shadow-lg text-center hover:from-green-600 hover:to-green-700 transition-all duration-300">
                <Phone className="h-8 w-8 text-white mx-auto mb-3" />
                <h4 className="text-xl font-bold text-white mb-2" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>WhatsApp पर चैट करें</h4>
                <p className="text-green-100 text-sm" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>तुरंत उत्तर पाएं</p>
              </div>
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;